/**
 * useNotifications Hook (Web Version)
 * Web-compatible notifications using Web Notifications API
 */

import { useEffect, useState } from 'react';
import {
  initializeNotifications,
  setCurrentlyViewingGroup,
} from '@/services/notifications/notificationService';
import { registerPushToken } from '@/services/notifications/pushTokenService';
import { useAuthStore } from '@/store/authStore';

export interface NotificationData {
  groupId: string;
  messageId?: string;
  senderId?: string;
  senderName?: string;
  type?: 'message' | 'mention' | 'reply';
}

// Global navigation ref for handling notification taps
let navigationRef: any = null;

export const setNotificationNavigationRef = (ref: any) => {
  navigationRef = ref;
  console.log('✅ [useNotifications:Web] Navigation ref set');
};

export const useNotifications = (shouldSetupNavigation: boolean = false) => {
  const { user } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  // Initialize web notifications on mount
  useEffect(() => {
    if (!user) {
      console.log('⏩ [useNotifications:Web] No user, skipping setup');
      return;
    }

    const setup = async () => {
      console.log(`🚀 [useNotifications:Web] Setting up web notifications...`);

      // Initialize notification system (checks Web Notifications API support)
      const initialized = await initializeNotifications();
      setIsInitialized(true); // Always mark as initialized on web
      setHasPermission(initialized);

      if (!initialized) {
        console.log(`⏩ [useNotifications:Web] Web notifications not available or denied`);
        return;
      }

      console.log(`✅ [useNotifications:Web] Web notifications initialized`);

      // Register push subscription with backend
      await registerPushToken();

      // Set up web-specific notification listeners
      setupWebNotificationListeners();
    };

    setup();

    // Cleanup on unmount
    return () => {
      console.log('🧹 [useNotifications:Web] Cleaning up notification listeners');
    };
  }, [user]);

  // Setup web notification event listeners
  const setupWebNotificationListeners = () => {
    // Listen for in-app notification events (for browsers that don't support Web Push)
    window.addEventListener('inAppNotification', handleInAppNotification as EventListener);

    // Service Worker message listener (for Web Push notifications)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    console.log('✅ [useNotifications:Web] Event listeners registered');
  };

  // Handle in-app notifications
  const handleInAppNotification = (event: CustomEvent) => {
    const { title, body, data } = event.detail;
    console.log(`🔔 [useNotifications:Web] In-app notification:`, title);

    // You could show a toast/banner here for in-app notifications
    // For now, just log it
  };

  // Handle messages from Service Worker
  const handleServiceWorkerMessage = (event: MessageEvent) => {
    if (event.data?.type === 'NOTIFICATION_CLICKED') {
      const { groupId } = event.data;
      console.log(`👆 [useNotifications:Web] Notification clicked for group:`, groupId);

      // Navigate to the group if navigation ref is available
      if (navigationRef && groupId) {
        navigationRef.navigate('GroupChat', { groupId });
      }
    }
  };

  // Mark currently viewing group (to suppress notifications)
  const setViewingGroup = (groupId: string | null) => {
    setCurrentlyViewingGroup(groupId);
  };

  return {
    isInitialized,
    hasPermission,
    setViewingGroup, // match native API
  };
};

export default useNotifications;
